import React from 'react';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { FaTicketAlt, FaUser, FaDesktop, FaCalendarAlt, FaUserSlash } from 'react-icons/fa';
import ITStaffAssignmentWidget from './ITStaffAssignmentWidget';

/**
 * Component to display ticket statistics with visualizations
 */
const TicketStatsDashboard = ({ 
  ticketStats, 
  ticketSystems, 
  ticketDailyData, 
  itUsers, 
  onFilterByStaff, 
  selectedStaffFilter,
  totalTickets 
}) => {
  // Bar chart data for tickets by system
  const systemBarChartData = {
    labels: ticketSystems.map(system => system.name.length > 20 ? system.name.substring(0, 20) + '...' : system.name),
    datasets: [
      {
        label: 'Tickets',
        data: ticketSystems.map(system => system.tickets),
        backgroundColor: 'rgba(29, 186, 172, 0.7)',
        borderColor: 'rgba(29, 186, 172, 1)',
        borderWidth: 1
      }
    ]
  };

  // Bar chart options for tickets by system
  const systemBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Tickets by System',
        font: {
          size: 14
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  // Doughnut chart data for ticket statuses
  const statusDoughnutData = {
    labels: ['Open', 'Not Assigned', 'Escalated', 'Closed'],
    datasets: [
      {
        data: [
          ticketStats.open - ticketStats.provider - ticketStats.not_assigned, 
          ticketStats.not_assigned, 
          ticketStats.provider,
          ticketStats.closed
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(75, 192, 192, 0.7)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  // Doughnut chart options for ticket statuses
  const statusDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          boxWidth: 15
        }
      },
      title: {
        display: true,
        text: 'Ticket Status Distribution',
        font: {
          size: 14
        }
      }
    },
    cutout: '60%'
  };

  // Line chart data for daily ticket trends
  const dailyLineChartData = {
    labels: ticketDailyData.dates.map(date => {
      const d = new Date(date);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }),
    datasets: [
      {
        label: 'Created',
        data: ticketDailyData.created,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      },
      {
        label: 'Closed',
        data: ticketDailyData.closed,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      }
    ]
  };

  // Line chart options for daily ticket trends
  const dailyLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: '30-Day Ticket Trend',
        font: {
          size: 14
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    }
  };

  // Calculate summary metrics
  const calculatedTotalTickets = totalTickets || ticketStats.open + ticketStats.closed;
  const resolutionRate = calculatedTotalTickets > 0 ? Math.round((ticketStats.closed / calculatedTotalTickets) * 100) : 0;
  const openTicketsPercentage = calculatedTotalTickets > 0 ? Math.round((ticketStats.open / calculatedTotalTickets) * 100) : 0;
  const escalationRate = ticketStats.open > 0 ? Math.round((ticketStats.provider / ticketStats.open) * 100) : 0;

  return (
    <div>
      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Tickets Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{calculatedTotalTickets}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FaTicketAlt className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-gray-500">
              <span>Resolution Rate</span>
              <span className="ml-auto font-semibold">{resolutionRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-blue-600 h-2 rounded-full" 
                style={{ width: `${resolutionRate}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Open Tickets Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{ticketStats.open}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <FaUser className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-gray-500">
              <span>Not Assigned</span>
              <span className="ml-auto font-semibold">{ticketStats.not_assigned}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-yellow-600 h-2 rounded-full" 
                style={{ width: `${openTicketsPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Escalated Tickets Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Escalated Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{ticketStats.provider}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <FaDesktop className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-gray-500">
              <span>Escalation Rate</span>
              <span className="ml-auto font-semibold">{escalationRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-red-600 h-2 rounded-full" 
                style={{ width: `${escalationRate}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Closed Tickets Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Closed Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{ticketStats.closed}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FaCalendarAlt className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-gray-500">
              <span>Last 30 Days</span>
              <span className="ml-auto font-semibold">{ticketStats.closed}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ width: `${resolutionRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg gap-6 mb-6">
        {/* Systems Chart - Left side (2/3 width) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-4" style={{ height: '300px' }}>
            <div className="h-full">
              {ticketSystems.length > 0 ? (
                <div style={{ height: '100%' }}>
                  <Bar 
                    data={{
                      labels: ticketSystems.map(system => 
                        system.name.length > 20 ? system.name.substring(0, 20) + '...' : system.name
                      ),
                      datasets: [{
                        label: 'Tickets by System',
                        data: ticketSystems.map(system => system.tickets),
                        backgroundColor: '#1DBAAC',
                        borderColor: '#1DBAAC',
                        borderWidth: 1
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        title: {
                          display: true,
                          text: 'Tickets by System',
                          font: { size: 16 }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { precision: 0 },
                          grid: { display: false, drawBorder: false }
                        },
                        x: {
                          grid: { display: false, drawBorder: false }
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Loading systems data...</p>
                </div>
              )}
            </div>
          </div>
        </div>
         
      </div>

      {/* Charts Row - moved below */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 lg:col-span-2">
          <div style={{ height: '300px' }}>
            <Doughnut data={statusDoughnutData} options={statusDoughnutOptions} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div style={{ height: '300px' }}>
            <Line data={dailyLineChartData} options={dailyLineChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketStatsDashboard;
